import type { BizyUiBlock } from '../../../schemas/bizy-ui-blocks';
import { ErrorBoundary } from '../../error/error-boundary';
import { bizyUiBlockRegistry } from './registry';
import { UnknownBlockView } from './unknown-block';

/**
 * Looks `block.type` up in {@link bizyUiBlockRegistry} and renders the
 * matching component. Unrecognized types fall through to
 * {@link UnknownBlockView} so an older client can still render a
 * newer conversation gracefully.
 *
 * Each card render is wrapped in an ErrorBoundary so that a single bad
 * card payload (corrupt persisted data, schema drift, runtime exception
 * in a renderer) cannot tear down the message list or the side panel —
 * which would otherwise make the chat header (and the History menu
 * button on it) unreachable. A render failure shows the same graceful
 * placeholder we use for unknown block types.
 */
export function UiBlockRenderer({ block, fontSize, isReadOnly = false }: { block: BizyUiBlock; fontSize?: 'sm' | 'md'; isReadOnly?: boolean }) {
	const Renderer = bizyUiBlockRegistry[block.type];
	if (!Renderer) {
		return <UnknownBlockView block={block} />;
	}
	return (
		<ErrorBoundary fallback={() => <UnknownBlockView block={block} />}>
			<Renderer block={block} fontSize={fontSize} isReadOnly={isReadOnly} />
		</ErrorBoundary>
	);
}
